import { CommercializationPercentFields } from '@/features/quotes/components/commercialization-percent-fields.component';
import { useQuoteForm } from '@/features/quotes/hooks/common/use-quote-form.hook';
import {
  commercializationPercentDefaultValues,
  validateAgencing,
  validateBrokerage,
  validateCommercialLimit,
} from '@/features/quotes/schemas/commercialization-percent.schema';
import { Button, Spinner, Text } from '@noorden-insurtech/alba-design-system';
import { ArrowLeft, Calculator } from 'lucide-react';
import { useStepCommercializationViewModel } from './hooks/use-step-commercialization-view-model.hook';

type Props = {
  onNext: () => void;
  onBack: () => void;
  canGoNext?: boolean;
  canGoBack?: boolean;
  isViewMode?: boolean;
};

/** Primeiro erro do campo, normalizado para string (o validator devolve string). */
function firstError(errors?: Array<unknown>): string | undefined {
  const error = errors?.[0];
  if (!error) return undefined;
  return typeof error === 'string'
    ? error
    : (error as { message?: string }).message;
}

export function StepCommercialization({
  onNext,
  onBack,
  canGoNext = true,
  canGoBack = true,
  isViewMode = false,
}: Props) {
  const form = useQuoteForm({
    defaultValues: commercializationPercentDefaultValues,
  });
  const { handleSubmit, isPending, isCalculating } =
    useStepCommercializationViewModel({ form, onNext });

  const isBusy = isPending || isCalculating;

  return (
    <div className='flex flex-col gap-8'>
      <div className='flex flex-col gap-6'>
        <Text className='text-lg font-bold text-alba-black-500'>
          Comercialização
        </Text>

        <div className='w-full'>
          <form.Subscribe
            selector={state => state.values}
            children={values => (
              <form.AppField
                name='brokeragePercent'
                validators={{
                  onChange: ({ value }) => validateBrokerage(value),
                }}
                children={brokerageField => (
                  <form.AppField
                    name='agencingPercent'
                    validators={{
                      onChange: ({ value }) => validateAgencing(value),
                    }}
                    children={agencingField => (
                      <CommercializationPercentFields
                        brokerageValue={brokerageField.state.value}
                        agencingValue={agencingField.state.value}
                        onBrokerageChange={brokerageField.handleChange}
                        onAgencingChange={agencingField.handleChange}
                        brokerageError={firstError(
                          brokerageField.state.meta.errors
                        )}
                        agencingError={firstError(
                          agencingField.state.meta.errors
                        )}
                        limitError={validateCommercialLimit(
                          values.brokeragePercent,
                          values.agencingPercent
                        )}
                        disabled={isViewMode}
                        autoFocusBrokerage={!isViewMode}
                      />
                    )}
                  />
                )}
              />
            )}
          />
        </div>
      </div>

      <div className='flex flex-row justify-between items-center px-6 gap-6 self-stretch'>
        <Button
          variant='ghost'
          size='lg'
          className='flex items-center gap-2'
          onClick={onBack}
          disabled={isViewMode || !canGoBack || isBusy}
        >
          <ArrowLeft className='w-5 h-5' strokeWidth={1.6} />
          Voltar
        </Button>
        <Button
          variant='primary'
          size='md'
          className='flex items-center gap-2 px-8 py-2 rounded-xl'
          onClick={handleSubmit}
          disabled={isViewMode || !canGoNext || isBusy}
        >
          {isPending ? (
            <>
              Salvando...
              <Spinner size='sm' colorScheme='gray' />
            </>
          ) : isCalculating ? (
            <>
              Calculando...
              <Spinner size='sm' colorScheme='gray' />
            </>
          ) : (
            <>
              Calcular
              <Calculator className='w-5 h-5' strokeWidth={1.6} />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
